/*
  Загрузка идентификаторов должников файлом - §6.4.2.

  Зачем это здесь. Паспорт, ИНН и СНИЛС можно внести на карточке счёта руками,
  но по одному человеку. У якорного клиента 6820 физических лиц с паспортными
  данными, и через форму их не вносят - а пока их нет, каждое заявление уходит с
  ходатайством об истребовании, суд тратит на это отдельное определение, и приказ
  выносится позже.

  Памятка «где это взять» стоит НА экране, а не в инструкции сбоку - по той же
  причине, что у выгрузки 1С: 16.09.2026 специалист заказчика не нашёл нужный
  отчёт в своей же базе и сказал, что такого в программе нет.

  Два обращения на загрузку: сперва «кому что ляжет», потом запись по
  подтверждению. Здесь это важнее, чем где-либо: файл правит сведения, которыми
  заявление называет суду конкретного человека.

  🔴 С 20.10.2026 файл уходит на сервер ОДИН раз и разбирается один раз
  (решение владельца 19.10.2026, `useUploadFlow`): «Посмотреть» и запись
  называют уже прочитанный файл номером, запись идёт фоновой работой с ходом,
  конец - блок «Готово»; перечень изменений остаётся под ним.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { rows as rowsCount } from '../api/format.js'
import { Notice } from './Common.jsx'
import UploadEnd from './upload/UploadEnd.jsx'
import UploadStages from './upload/UploadStages.jsx'
import useUploadFlow from './upload/useUploadFlow.js'
import UploadHelp from './UploadHelp.jsx'

/*
  Образец файла с идентификаторами. Вторая строка намеренно неполная: видно, что
  достаточно одного идентификатора, а остальные графы могут быть пусты.
*/
const SAMPLE = {
  file: 'идентификаторы-образец.csv',
  /* Место рождения добавлено 30.09.2026: в выписке из домовой книги эта графа
     заполнена, а заявление называет его рядом с датой рождения, если оно
     известно взыскателю. */
  headers: ['Лицевой счёт', 'ФИО', 'Дата рождения', 'Место рождения', 'Паспорт', 'ИНН', 'СНИЛС'],
  rows: [
    [
      '20346',
      'Веселков Александр Александрович',
      '08.09.2008',
      'г. Москва',
      '4523 160847',
      '',
      '',
    ],
    ['11852', 'Разважная Елена Александровна', '18.07.1981', '', '', '504400000011', ''],
  ],
}

const SHOWN_ISSUES = 20
/* Сколько строк «требует внимания» показать. Полное число стоит рядом: список в
   тысячу строк не читается, а двадцати хватает, чтобы понять, что не так с
   файлом, - остальное чинится тем же исправлением. */

function Plan({ plan }) {
  return (
    <div className="onec__summary">
      <p>
        Строк в файле: <b>{rowsCount(plan.total)}</b>. Будет внесено: <b>{plan.written}</b>.{' '}
        {plan.unchanged > 0 ? <>Уже совпадает: {plan.unchanged}.</> : null}
      </p>
      {plan.changes.slice(0, SHOWN_ISSUES).map((change) => (
        <p key={change.person_id}>
          Счёт {change.account_number}, {change.full_name}: {change.fields.join(', ')}.
        </p>
      ))}
      {plan.issues.length > 0 ? (
        <>
          <p className="onec__skipped">
            Требуют внимания: {rowsCount(plan.issues.length)}. {plan.issues.length === 1 ? 'Она не записана.' : 'Они не записаны.'}
          </p>
          {plan.issues.slice(0, SHOWN_ISSUES).map((issue) => (
            <p key={`${issue.row_number}-${issue.account_number ?? ''}`} className="onec__skipped">
              Строка {issue.row_number}: {issue.explanation}
            </p>
          ))}
        </>
      ) : null}
    </div>
  )
}

export default function IdentityUpload({ onLoaded }) {
  const flow = useUploadFlow('identities')
  const file = flow.stored
  const [plan, setPlan] = useState(null)
  const [written, setWritten] = useState(false)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  // Номер захода: новый заход пересоздаёт поле выбора без имени прежнего файла.
  const [round, setRound] = useState(0)

  function choose(chosen) {
    setPlan(null)
    setWritten(false)
    setError(null)
    flow.choose(chosen ?? null)
  }

  function again() {
    choose(null)
    setRound(round + 1)
  }

  async function act(action, then) {
    setBusy(true)
    setError(null)
    try {
      then(await action())
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const look = () =>
    act(
      () =>
        flow.check(
          () => api.previewIdentities(file),
          (seen) => `строк в файле: ${seen.total}, будет внесено: ${seen.written}`,
        ),
      setPlan,
    )

  // Запись - фоновой работой по прочитанному файлу (20.10.2026); ответ тот же,
  // что отдавал запрос записи: сервер вызывает тот же обработчик.
  const load = () =>
    act(
      () => flow.write('identities', { column_overrides: null }),
      (done) => {
        setPlan(done)
        setWritten(true)
        // Чек-лист готовности считает опознанных должников: после записи он
        // обязан пересчитаться, иначе человек видит «нет» по только что
        // внесённым паспортам.
        onLoaded()
      },
    )

  return (
    <>
      <UploadHelp
        title="Где это взять в 1С"
        where={[
          'Готового отчёта с паспортами в 1С нет: они лежат в регистре сведений ' +
            '«Документы физических лиц», а отчёты по начислениям строятся по регистру ' +
            'взаиморасчётов. Возьмите их универсальным отчётом: «Отчёты» → «Универсальный ' +
            'отчёт», в верхней строке выбрать «Регистр сведений» и «Документы физических лиц».',
          'В настройках отчёта выведите колонки: физическое лицо, вид документа, серия, ' +
            'номер, дата выдачи. Лицевой счёт добавьте из справочника жильцов - он говорит, ' +
            'в чьём портфеле искать человека. Сохраните: «Ещё» → «Сохранить» → xlsx или CSV.',
        ]}
        sample={SAMPLE}
        note={
          'Достаточно одного идентификатора из трёх: пустое поле означает «неизвестен», и ' +
          'подаче это не препятствует - суд запросит сведения сам (пункт 3 части 2 статьи ' +
          '124 ГПК РФ). Фамилия обязательна: по ней строка находит человека, а один счёт ' +
          'бывает записан на нескольких.'
        }
      />

      <section className="section card">
        <div className="field field--l">
          <label className="field__control">
            <span className="field__label">Файл с идентификаторами</span>
            <input
              key={`${round}-${flow.attempt}`}
              className="field__file"
              type="file"
              accept=".csv,.xlsx"
              disabled={busy || flow.busy}
              onChange={(event) => choose(event.target.files?.[0])}
            />
          </label>
        </div>
        <UploadStages flow={flow} />

        <div className="row">
          <button type="button" className="button" disabled={busy || !file || written} onClick={look}>
            Посмотреть, что получится
          </button>
          <button
            type="button"
            className="button"
            disabled={busy || !file || !plan || written}
            onClick={load}
          >
            Внести в портфель
          </button>
        </div>

        <Notice error={flow.error ?? error} />
        {/* Конец - блок «Готово» (20.10.2026); перечень изменений - под ним. */}
        {written ? (
          <UploadEnd
            title="Готово: идентификаторы внесены в портфель"
            facts={[
              ['Строк в файле', plan.total],
              ['Внесено сведений', plan.written],
              ['Уже совпадало', plan.unchanged || null],
              ['Строк, оставшихся вам', plan.issues.length],
            ]}
            seconds={flow.written?.seconds}
            onAgain={again}
          />
        ) : null}
        {plan ? <Plan plan={plan} /> : null}
      </section>
    </>
  )
}
